import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { OrderLine } from "@/api/order/_models/Order";
import { formatCurrency } from "@/features/order/orderList.utils";
import { ListGridExpandedSection } from "@/components/list-grid/ListGridExpandedSection";

interface OrderListExpandedLineItemsProps {
  lines: OrderLine[];
}

const toUnitCount = (lines: OrderLine[]): number => {
  return lines.reduce((count, line) => count + (line.orderedQuantity ?? 0), 0);
};

const toOrderTotal = (lines: OrderLine[]): number | undefined => {
  const totals = lines.flatMap((line) => (line.total === undefined ? [] : [line.total]));
  if (totals.length === 0) return undefined;
  return totals.reduce((sum, total) => sum + total, 0);
};

const toQuantityText = (line: OrderLine): string => {
  const quantity = line.orderedQuantity ?? "—";
  return `${quantity} × ${formatCurrency(line.unitPrice)}`;
};

/** The expanded order's line items, titled by unit count: each line's SKU, quantity × unit price and total, then the order total. */
export const OrderListExpandedLineItems = (props: OrderListExpandedLineItemsProps) => {
  const { lines } = props;
  const units = toUnitCount(lines);
  let title = `${units} items`;
  if (units === 1) title = "1 item";
  if (lines.length === 0) title = "Items";

  return (
    <Box className="OrderListExpanded-items">
      <ListGridExpandedSection
        emptyText="No lines."
        isEmpty={lines.length === 0}
        title={title}
        slotProps={{
          title: {
            sx: {
              position: "relative",
              left: -2
            }
          }
        }}
      >
        <Stack spacing={1}>
          {lines.map((line) => (
            <Stack
              direction="row"
              key={line.id}
              spacing={1.5}
              sx={{ alignItems: "baseline" }}
            >
              <Typography
                noWrap
                sx={{
                  flex: 1,
                  fontFamily: "monospace",
                  minWidth: 0,
                }}
                variant="body2"
              >
                {line.sku}
              </Typography>
              <Typography
                color="text.secondary"
                sx={{
                  fontVariantNumeric: "tabular-nums",
                  whiteSpace: "nowrap",
                }}
                variant="body2"
              >
                {toQuantityText(line)}
              </Typography>
              <Typography
                sx={{
                  fontVariantNumeric: "tabular-nums",
                  minWidth: 96,
                  textAlign: "right",
                  whiteSpace: "nowrap",
                }}
                variant="body2"
              >
                {formatCurrency(line.total)}
              </Typography>
            </Stack>
          ))}
        </Stack>
        {lines.length > 0 && (
          <Stack
            direction="row"
            sx={{
              borderTop: 1,
              borderColor: "divider",
              justifyContent: "space-between",
              mt: 1.5,
              pt: 1.5,
            }}
          >
            <Typography
              sx={{ fontWeight: 600 }}
              variant="body2"
            >
              Order total
            </Typography>
            <Typography
              sx={{
                fontVariantNumeric: "tabular-nums",
                fontWeight: 600,
              }}
              variant="body2"
            >
              {formatCurrency(toOrderTotal(lines))}
            </Typography>
          </Stack>
        )}
      </ListGridExpandedSection>
    </Box>
  );
};
